import { Link } from 'react-router-dom';
import { Plane, Briefcase, Heart, Clock, Users, Shield, Wifi, Coffee, MapPin } from 'lucide-react';


import airportImage from '../assets/images/airport-transfers.webp';
import corporateImage from '../assets/images/corporate-transportation.webp';
import specialOccasionImage from '../assets/images/special-occasions.webp';
import hourlyServiceImage from '../assets/images/hourly-service.webp';
import groupTransportImage from '../assets/images/group-transportation.webp';
import cityToursImage from '../assets/images/city-tours.webp';

export default function Services() {
  const services = [
    {
      icon: Plane,
      title: "Airport Transfers",
      description: "Professional, reliable airport transportation with flight tracking and meet & greet service.",
      features: ["Flight tracking", "Meet & greet service", "Luggage assistance", "On-time guarantee"],
      image: airportImage
    },
    {
      icon: Briefcase,
      title: "Corporate Transportation",
      description: "Executive transportation for business meetings, conferences, and corporate events.",
      features: ["Professional chauffeurs", "Punctual service", "Confidentiality", "Flexible scheduling"],
      image: corporateImage
    },
    {
      icon: Heart,
      title: "Special Occasions",
      description: "Make your special moments unforgettable with our luxury transportation services.",
      features: ["Wedding transportation", "Anniversary celebrations", "Birthday parties", "Custom decorations"],
      image: specialOccasionImage
    },
    {
      icon: Clock,
      title: "Hourly Service",
      description: "Flexible hourly transportation for shopping, sightseeing, or multiple destinations.",
      features: ["Minimum 3 hours", "Wait time included", "Multiple stops", "Flexible routing"],
      image: hourlyServiceImage
    },
    {
      icon: Users,
      title: "Group Transportation",
      description: "Comfortable group transportation for events, tours, and group outings.",
      features: ["Up to 8 passengers", "Group discounts", "Event coordination", "Custom itineraries"],
      image: groupTransportImage
    },
    {
      icon: MapPin,
      title: "City Tours",
      description: "Discover the city in luxury with our guided tour services and local expertise.",
      features: ["Local guide available", "Custom routes", "Historical sites", "Photography stops"],
      image: cityToursImage
    }
  ];

  return (
    <div className="min-h-screen bg-white ">
      {/* Hero Section */}
      <section className="py-20 bg-gray-50 animate-fade-in-up " >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center ">
          <h1 className="text-5xl md:text-6xl font-playfair font-bold text-deep-black mb-6 ">
            Our <span className="text-luxury-gold">Services</span>
          </h1>
          <p className="text-xl text-charcoal max-w-3xl mx-auto mb-8">
            Experience the finest in luxury transportation with our comprehensive range of professional services, 
            designed to meet your every need with elegance and reliability.
          </p>
          <Link to="/contact">
            <button className="bg-luxury-gold  rounded text-deep-black hover:bg-yellow-600 font-semibold px-8 py-4 text-lg">
              Request a Quote
            </button>
          </Link>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const IconComponent = service.icon;
              return (
                <div 
                  key={index} 
                  className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
                >
                  <div 
                    className="h-48 bg-cover bg-center"
                    style={{ backgroundImage: `url(${service.image})` }}
                  />
                  <div className="p-6">
                    <div className="flex items-center mb-4">
                      <IconComponent className="text-luxury-gold h-6 w-6 mr-3" />
                      <h3 className="text-xl font-playfair font-semibold">{service.title}</h3>
                    </div>
                    <p className="text-gray-600 mb-4">{service.description}</p>
                    <ul className="space-y-2 mb-6">
                      {service.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-center text-sm text-gray-600">
                          <div className="w-2 h-2 bg-luxury-gold rounded-full mr-3 flex-shrink-0"></div>
                          {feature}
                        </li>
                      ))}
                    </ul>
                    <div className="flex justify-center">
                      <Link to="/contact">
                        <button className="bg-luxury-gold rounded text-deep-black hover:bg-yellow-600 font-semibold px-6 py-2">
                          Book Now
                        </button>
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Fleet Features */}
      <section className="py-20 bg-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-playfair font-bold text-white mb-6">
              Why Choose Our <span className="text-luxury-gold">Suburban Fleet</span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Our meticulously maintained Chevrolet Suburbans offer the perfect combination of luxury, safety, and comfort.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center p-6 bg-white rounded-xl shadow-lg">
              <Users className="text-luxury-gold h-12 w-12 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2 text-deep-black">8 Passengers</h3>
              <p className="text-gray-600">Spacious seating for up to 8 passengers with premium leather interior</p>
            </div>
            <div className="text-center p-6 bg-white rounded-xl shadow-lg">
              <Wifi className="text-luxury-gold h-12 w-12 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2 text-deep-black">Wi-Fi & Entertainment</h3>
              <p className="text-gray-600">Complimentary Wi-Fi and premium entertainment system</p>
            </div>
            <div className="text-center p-6 bg-white rounded-xl shadow-lg">
              <Shield className="text-luxury-gold h-12 w-12 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2 text-deep-black">Safety Features</h3>
              <p className="text-gray-600">Latest safety technology and professionally trained chauffeurs</p>
            </div>
            <div className="text-center p-6 bg-white rounded-xl shadow-lg">
              <Coffee className="text-luxury-gold h-12 w-12 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2 text-deep-black">Premium Amenities</h3>
              <p className="text-gray-600">Climate control, refreshments, and luxury comfort features</p>
            </div>
          </div>
        </div>
      </section>

      {/* Booking Process */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-playfair font-bold text-deep-black mb-6">
              Simple <span className="text-luxury-gold">Booking Process</span>
            </h2>
            <p className="text-xl text-charcoal max-w-3xl mx-auto">
              Booking your luxury transportation is easy with our streamlined process.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-luxury-gold text-deep-black w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                1
              </div>
              <h3 className="text-xl font-semibold mb-4">Contact Us</h3>
              <p className="text-gray-600">
                Reach out via phone, email, or our online form with your transportation needs and preferences.
              </p>
            </div>
            <div className="text-center">
              <div className="bg-luxury-gold text-deep-black w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                2
              </div>
              <h3 className="text-xl font-semibold mb-4">Get a Quote</h3>
              <p className="text-gray-600">
                Receive a detailed quote based on your specific requirements, including all services and amenities.
              </p>
            </div>
            <div className="text-center">
              <div className="bg-luxury-gold text-deep-black w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                3
              </div>
              <h3 className="text-xl font-semibold mb-4">Enjoy Your Ride</h3>
              <p className="text-gray-600">
                Relax and enjoy premium transportation service with our professional chauffeurs and luxury vehicles.
              </p>
            </div>
          </div>

          <div className="text-center mt-12">
            <Link to="/contact">
              <button className="bg-luxury-gold rounded text-deep-black hover:bg-yellow-600 font-semibold px-8 py-4 text-lg">
                Start Your Booking
              </button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}